import React, { useState, useEffect } from 'react';
import { KeyRound, CheckCircle, AlertTriangle, Eye, EyeOff, Trash2 } from 'lucide-react';
import api from '../../utils/api';

const GroqApiKeySetup = ({ onKeyUpdate }) => {
  const [status, setStatus] = useState({ isSet: false, maskedKey: null });
  const [loading, setLoading] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchStatus = async () => {
    try {
      const res = await api('/tenant-settings');
      if (res.ok) {
        const data = await res.json();
        setStatus({ isSet: data.groqApiKeySet, maskedKey: data.maskedGroqApiKey });
        if (onKeyUpdate) onKeyUpdate(data.groqApiKeySet);
      }
    } catch (err) {
      console.error('Error fetching API key status:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    
    setSaving(true);
    try {
      const res = await api('/tenant-settings', {
        method: 'POST',
        body: { groqApiKey: inputValue.trim() }
      });
      if (res.ok) {
        setInputValue('');
        await fetchStatus();
      } else {
        alert('Failed to save API key');
      }
    } catch (err) {
      console.error(err);
      alert('Error saving API key');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Are you sure you want to remove the API key? AI features will stop working.')) return;
    
    setSaving(true);
    try {
      const res = await api('/tenant-settings', {
        method: 'POST',
        body: { groqApiKey: null }
      });
      if (res.ok) {
        await fetchStatus();
      }
    } catch (err) {
      console.error(err);
      alert('Error removing API key');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: '24px', textAlign: 'center' }}>Loading API Key Settings...</div>;

  return (
    <div style={{ 
      background: 'white', 
      padding: '24px', 
      borderRadius: '12px', 
      border: `1px solid ${status.isSet ? 'var(--color-border-primary)' : '#fca5a5'}`,
      boxShadow: status.isSet ? 'none' : '0 4px 6px -1px rgba(239, 68, 68, 0.1)',
      marginBottom: '24px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <div style={{ 
          background: status.isSet ? '#dcfce7' : '#fee2e2', 
          padding: '10px', 
          borderRadius: '10px' 
        }}>
          <KeyRound size={20} color={status.isSet ? '#16a34a' : '#ef4444'} />
        </div>
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>Groq API Key</h2>
          <p style={{ color: 'var(--color-text-secondary)', margin: '4px 0 0 0', fontSize: '14px' }}>
            Powered by LLaMA 3. Required for all AI features.
          </p>
        </div>
      </div>

      {status.isSet ? (
        <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid var(--color-border-tertiary)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckCircle size={18} color="#16a34a" />
              <span style={{ fontWeight: '600', color: '#1e293b' }}>API Key is active</span>
            </div>
            <button 
              onClick={handleRemove}
              disabled={saving}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '6px 12px', background: 'none', border: '1px solid #ef4444',
                color: '#ef4444', borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
                fontWeight: '600'
              }}
            >
              <Trash2 size={14} /> Remove Key
            </button>
          </div>
          <div style={{ marginTop: '12px', fontFamily: 'monospace', color: 'var(--color-text-secondary)', fontSize: '14px' }}>
            Key: {status.maskedKey}
          </div>
        </div>
      ) : (
        <div style={{ background: '#fef2f2', padding: '16px', borderRadius: '8px', border: '1px solid #fecaca' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', color: '#b91c1c' }}>
            <AlertTriangle size={18} />
            <span style={{ fontWeight: '600' }}>API Key is not configured. AI features are disabled.</span>
          </div>
          <form onSubmit={handleSave} style={{ display: 'flex', gap: '12px' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <input 
                type={showKey ? 'text' : 'password'}
                placeholder="Enter Groq API Key (gsk_...)"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                style={{
                  width: '100%', padding: '10px 40px 10px 12px', borderRadius: '8px',
                  border: '1px solid #f87171', outline: 'none'
                }}
              />
              <button 
                type="button"
                onClick={() => setShowKey(!showKey)}
                style={{
                  position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8'
                }}
              >
                {showKey ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <button 
              type="submit"
              disabled={saving || !inputValue.trim()}
              style={{
                padding: '0 24px', background: '#3b82f6', color: 'white', border: 'none',
                borderRadius: '8px', fontWeight: 'bold', cursor: saving || !inputValue.trim() ? 'not-allowed' : 'pointer',
                opacity: saving || !inputValue.trim() ? 0.7 : 1
              }}
            >
              {saving ? 'Saving...' : 'Save Key'}
            </button>
          </form>
          <div style={{ marginTop: '12px', fontSize: '13px', color: '#7f1d1d' }}>
            You can get a free API key from <a href="https://console.groq.com/keys" target="_blank" rel="noopener noreferrer" style={{ color: '#dc2626', fontWeight: 'bold' }}>console.groq.com</a>.
          </div>
        </div>
      )}
    </div>
  );
};

export default GroqApiKeySetup;
